import React, { FC } from 'react';
import styled from '@emotion/styled';
import { HotelTarget, useButton, useCurrency, useI18n } from '~Hooks';
import { Box, GreenButton, GhostButton } from '~Styles';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import ErrorNote from '~Components/ErrorNote';
import RoomNumber from '../RoomNumber';

/** Rounded inset card, same as the info boxes elsewhere in the app. */
const SummaryBox = styled(Box)`
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
`;

interface Props {
  target: HotelTarget;
  amount: number;
  /** In minor units, entered before the search; booked on the hotel's tip code */
  tip: number;
  /** Why the last attempt was refused, shown under the buttons */
  notice?: string;
  onConfirm: () => void;
  onBack: () => void;
}

/** Last look at room, amount and tip before the bill is posted to the PMS. */
const ConfirmStep: FC<Props> = ({ target, amount, tip, notice, onConfirm, onBack }) => {
  const { i18n } = useI18n();
  const currency = useCurrency();
  const confirmButton = useButton({ onClick: onConfirm });
  const backButton = useButton({ onClick: onBack });

  return (
    <Box fluid display="flex" flexDirection="column" gap="medium">
      <SummaryBox fluid display="flex" flexDirection="column" gap="small" padding="medium">
        <Box display="flex" justifyContent="space-between" alignItems="center" gap="medium">
          <Heading level={4} color="var(--colorWhite)">
            {target.name}
          </Heading>
          <RoomNumber target={target} />
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap="medium">
          <Paragraph level={3} color="var(--colorBlueGray200)">
            {i18n('HotelRoom.Amount')}
          </Paragraph>
          {tip > 0 ? (
            <Paragraph level={3} color="var(--colorWhite)">
              {currency.format(amount)}
            </Paragraph>
          ) : (
            <Heading level={2} color="var(--colorWhite)">
              {currency.format(amount)}
            </Heading>
          )}
        </Box>
        {tip > 0 && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap="medium">
              <Paragraph level={3} color="var(--colorBlueGray200)">
                {i18n('HotelRoom.Tip')}
              </Paragraph>
              <Paragraph level={3} color="var(--colorWhite)">
                {currency.format(tip)}
              </Paragraph>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap="medium">
              <Paragraph level={3} color="var(--colorBlueGray200)">
                {i18n('HotelRoom.Total')}
              </Paragraph>
              <Heading level={2} color="var(--colorWhite)">
                {currency.format(amount + tip)}
              </Heading>
            </Box>
          </>
        )}
      </SummaryBox>

      <Box fluid style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 8 }}>
        <GhostButton {...backButton} size="large">
          {i18n('HotelRoom.Back')}
        </GhostButton>
        <GreenButton {...confirmButton} size="large">
          {i18n('HotelRoom.Charge')}
        </GreenButton>
      </Box>

      {notice && <ErrorNote>{notice}</ErrorNote>}
    </Box>
  );
};

export default ConfirmStep;
